import React, { useState } from 'react';
import { useLeaveRequests, useLeaveTypes, useEmployees, useCreateLeaveRequest, useApproveLeaveRequest, useRejectLeaveRequest } from '@/api';
import { Card, CardContent, CardHeader, CardTitle, Button, Input, Badge, Modal } from '@/components/ui';
import { LoadingSpinner, Pagination } from '@/components/common';
import { Plus, Check, X, Calendar } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { extractPaginatedData } from '@/utils/pagination';

const statusConfig: Record<string, { label: string; color: string }> = {
  pending: { label: 'Ожидание', color: 'bg-yellow-100 text-yellow-700' },
  approved: { label: 'Одобрено', color: 'bg-green-100 text-green-700' },
  rejected: { label: 'Отклонено', color: 'bg-red-100 text-red-700' },
  cancelled: { label: 'Отменено', color: 'bg-gray-100 text-gray-700' },
};

export default function LeaveRequestsPage() {
  const perPage = 10;
  const { can } = useAuth();
  const canManage = can('manage_leaves');
  const canApprove = can('approve_leaves');
  const [page, setPage] = useState(1);
  const [showForm, setShowForm] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [rejectNotes, setRejectNotes] = useState<{ [key: number]: string }>({});
  const [showRejectForm, setShowRejectForm] = useState<number | null>(null);

  const { data: leavesData, isLoading } = useLeaveRequests({ page, per_page: perPage, status: statusFilter });
  const { data: leaveTypesData } = useLeaveTypes();
  const { data: employeesData } = useEmployees({ per_page: 100, is_active: true });

  const createMutation = useCreateLeaveRequest();
  const approveMutation = useApproveLeaveRequest();
  const rejectMutation = useRejectLeaveRequest();

  const { items: leaves, meta } = extractPaginatedData<any>(leavesData, page, perPage);
  const leaveTypes: { value: string; label: string }[] = Array.isArray(leaveTypesData?.data)
    ? leaveTypesData.data
    : leaveTypesData?.data?.data || [];
  const leaveTypeLabels = Object.fromEntries(leaveTypes.map((type) => [type.value, type.label]));
  const { items: employees } = extractPaginatedData<any>(employeesData, 1, 100);

  const handleApprove = async (id: number) => {
    if (window.confirm('Одобрить этот запрос на отпуск?')) {
      await approveMutation.mutateAsync({ id });
    }
  };

  const handleReject = async (id: number) => {
    const notes = rejectNotes[id];
    if (!notes) {
      alert('Пожалуйста, укажите причину отклонения');
      return;
    }
    await rejectMutation.mutateAsync({ id, notes });
    setShowRejectForm(null);
    setRejectNotes((prev) => {
      const copy = { ...prev };
      delete copy[id];
      return copy;
    });
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="space-y-6 page-shell">
      <div className="page-toolbar">
        <h1 className="text-3xl font-bold">Запросы на отпуск</h1>
        {canManage && (
          <Button onClick={() => setShowForm(!showForm)}>
            <Plus className="w-4 h-4 mr-2" />
            Новый запрос
          </Button>
        )}
      </div>

      <Modal
        isOpen={showForm}
        onClose={() => setShowForm(false)}
        title="Создать запрос на отпуск"
      >
        <LeaveRequestForm
          employees={employees}
          leaveTypes={leaveTypes}
          onSubmit={async (data) => {
            await createMutation.mutateAsync(data);
            setShowForm(false);
          }}
          onCancel={() => setShowForm(false)}
          isLoading={createMutation.isPending}
        />
      </Modal>

      {/* Filters */}
      <div className="flex flex-col gap-4 sm:flex-row">
        <select
          value={statusFilter}
          onChange={(e) => {
            setPage(1);
            setStatusFilter(e.target.value);
          }}
          className="border rounded-md px-3 py-2"
        >
          <option value="">Все статусы</option>
          <option value="pending">Ожидание</option>
          <option value="approved">Одобрено</option>
          <option value="rejected">Отклонено</option>
        </select>
      </div>

      {/* Leave Requests Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto ui-table-wrap">
            <table className="w-full ui-table">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold">Сотрудник</th>
                  <th className="text-left py-3 px-4 font-semibold">Тип отпуска</th>
                  <th className="text-left py-3 px-4 font-semibold">Период</th>
                  <th className="text-left py-3 px-4 font-semibold">Дни</th>
                  <th className="text-left py-3 px-4 font-semibold">Причина</th>
                  <th className="text-left py-3 px-4 font-semibold">Статус</th>
                  <th className="text-left py-3 px-4 font-semibold">Действия</th>
                </tr>
              </thead>
              <tbody>
                {leaves.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="text-center py-10 text-gray-500">
                      Запросы на отпуск не найдены
                    </td>
                  </tr>
                ) : (
                  leaves.map((leave: any) => {
                    const config = statusConfig[leave.status] || statusConfig.pending;
                    const days = Number(leave.total_days || 0);

                    return (
                      <tr key={leave.id} className="border-b hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="font-medium">{leave.employee?.full_name}</div>
                          <div className="text-sm text-gray-500">{leave.employee?.department?.name}</div>
                        </td>
                        <td className="py-3 px-4">
                          <Badge variant="outline">{leaveTypeLabels[leave.leave_type] || leave.leave_type}</Badge>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4 text-gray-400" />
                            {leave.date_from} — {leave.date_to}
                          </div>
                        </td>
                        <td className="py-3 px-4">{days}</td>
                        <td className="py-3 px-4">
                          <span className="ui-wrap block max-w-xs text-sm text-gray-600">
                            {leave.reason || '-'}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
                            {config.label}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          {leave.status === 'pending' && canApprove && (
                            <div className="flex gap-1">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleApprove(leave.id)}
                                disabled={approveMutation.isPending}
                              >
                                <Check className="w-4 h-4 text-green-600" />
                              </Button>
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => setShowRejectForm(leave.id)}
                              >
                                <X className="w-4 h-4 text-red-600" />
                              </Button>
                            </div>
                          )}
                          {showRejectForm === leave.id && (
                            <Modal
                              isOpen={true}
                              onClose={() => setShowRejectForm(null)}
                              title="Отклонить запрос на отпуск"
                            >
                              <div className="space-y-4">
                                <textarea
                                  placeholder="Причина отклонения..."
                                  value={rejectNotes[leave.id] || ''}
                                  onChange={(e) =>
                                    setRejectNotes((prev) => ({ ...prev, [leave.id]: e.target.value }))
                                  }
                                  className="w-full border rounded-md px-3 py-2 h-24"
                                />
                                <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                                  <Button variant="outline" onClick={() => setShowRejectForm(null)}>
                                    Отмена
                                  </Button>
                                  <Button
                                    variant="destructive"
                                    onClick={() => handleReject(leave.id)}
                                    disabled={rejectMutation.isPending}
                                  >
                                    Отклонить
                                  </Button>
                                </div>
                              </div>
                            </Modal>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
      <Pagination meta={meta} onPageChange={setPage} />
    </div>
  );
}

interface LeaveRequestFormProps {
  employees: any[];
  leaveTypes: { value: string; label: string }[];
  onSubmit: (data: {
    employee_id: number;
    leave_type: string;
    date_from: string;
    date_to: string;
    reason?: string;
  }) => Promise<void>;
  onCancel: () => void;
  isLoading: boolean;
}

function LeaveRequestForm({ employees, leaveTypes, onSubmit, onCancel, isLoading }: LeaveRequestFormProps) {
  const [employeeId, setEmployeeId] = useState('');
  const [leaveType, setLeaveType] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [reason, setReason] = useState('');

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      employee_id: Number(employeeId),
      leave_type: leaveType,
      date_from: dateFrom,
      date_to: dateTo,
      reason: reason || undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium mb-1">Сотрудник *</label>
          <select
            value={employeeId}
            onChange={(e) => setEmployeeId(e.target.value)}
            required
            className="w-full border rounded-md px-3 py-2"
          >
            <option value="">Выберите сотрудника</option>
            {employees.map((emp: any) => (
              <option key={emp.id} value={emp.id}>
                {emp.full_name}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Тип отпуска *</label>
          <select
            value={leaveType}
            onChange={(e) => setLeaveType(e.target.value)}
            required
            className="w-full border rounded-md px-3 py-2"
          >
            <option value="">Выберите тип</option>
            {leaveTypes.map((type) => (
              <option key={type.value} value={type.value}>
                {type.label}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Дата начала *</label>
          <Input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            required
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1">Дата окончания *</label>
          <Input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            required
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium mb-1">Причина</label>
        <textarea
          placeholder="Необязательная причина для отпуска..."
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          className="w-full border rounded-md px-3 py-2 h-20"
        />
      </div>
      <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" onClick={onCancel}>
          Отмена
        </Button>
        <Button type="submit" disabled={isLoading}>
          {isLoading ? 'Отправка...' : 'Отправить запрос'}
        </Button>
      </div>
    </form>
  );
}


